// ---------------------------------------------------------------------------
// The receipt of a sale rung up with no connection, put on paper NOW.
//
// The same sheet the online path photographs, rendered off-screen from the
// figures the till itself charged - the lines, the rate it priced them at,
// the payments it took - under a receipt number the server reserved for this
// till in advance. The pixels go to the shop's print agent over 127.0.0.1,
// never to the unreachable API; the agent writes the job into the cloud's
// record when the cloud is back. A retry carries the same key, so the agent
// can refuse to print it twice.
//
// The layout comes from the receipt design the till captured with its
// catalogue while the connection was good, so an outage does not change
// what the paper looks like.
// ---------------------------------------------------------------------------

import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { useI18n } from '../../i18n/I18nProvider';
import { ReceiptSheet, type ReceiptShopDetails } from '../print/ReceiptSheet';
import { usePrintConfig } from '../../api/designs.api';
import { loadCachedCatalogue } from '../../lib/syncEngine';
import { printViaAgent } from '../../lib/printAgent';
import { recordPrint, updatePrint } from '../../lib/offlineDb';
import { dotsForRoll, rasteriseElement, RasterError } from '../../utils/rasterise';
import { toast } from '../../stores/toastStore';
import { FALLBACK_RECEIPT_CONFIG, type ReceiptConfig } from '../../types/design';
import type { Sale } from '../../types';

const OFF_SCREEN: CSSProperties = { position: 'fixed', insetInlineStart: '-200vw', top: 0, background: '#ffffff' };

export function OfflineReceiptPrint({
  offlineId,
  sale,
  shop,
  onSettled,
}: {
  /// The queued sale's own id; the print's key is derived from it.
  offlineId: string;
  sale: Sale;
  shop: ReceiptShopDetails;
  onSettled: () => void;
}) {
  const { t } = useI18n();
  const wrapper = useRef<HTMLDivElement>(null);
  const { data: active } = usePrintConfig();
  const [config, setConfig] = useState<ReceiptConfig | null>(null);
  const started = useRef(false);

  // The design captured with the catalogue first; the live one if the cache
  // somehow still has it; the built-in layout last.
  useEffect(() => {
    let disposed = false;
    void loadCachedCatalogue().then((catalogue) => {
      if (disposed) return;
      setConfig(catalogue?.printConfig?.receipt ?? active?.receipt ?? FALLBACK_RECEIPT_CONFIG);
    });
    return () => {
      disposed = true;
    };
  }, [active]);

  useEffect(() => {
    if (!config || started.current) return;
    started.current = true;
    const printId = `${offlineId}:receipt`;

    const run = async () => {
      try {
        const element = wrapper.current?.querySelector<HTMLElement>('.receipt-sheet');
        if (!element) throw new RasterError('That document could not be prepared.');
        const raster = await rasteriseElement(element, { pixelWidth: dotsForRoll(config.widthMm === 58 ? 58 : 80) });
        await recordPrint({
          printId,
          jobId: null,
          documentType: 'CASHIER_RECEIPT',
          printerRole: 'CASHIER_RECEIPT',
          state: 'pending',
          reference: sale.receiptNumber,
          isReprint: false,
          createdAt: new Date().toISOString(),
        }).catch(() => undefined);

        const result = await printViaAgent({
          key: printId,
          documentType: 'CASHIER_RECEIPT',
          printerRole: 'CASHIER_RECEIPT',
          printerProfileId: null,
          raster,
        });

        await updatePrint(printId, {
          state: result.status === 'SENT' ? 'printed' : 'failed',
          settledAt: new Date().toISOString(),
          ...(result.error ? { failedReason: result.error } : {}),
        }).catch(() => undefined);

        if (result.status === 'SENT') {
          toast.success(t.offline.receiptPrinted.replace('{number}', sale.receiptNumber));
        } else {
          toast.error(t.offline.receiptNotPrinted, result.error ?? undefined);
        }
      } catch (caught) {
        toast.error(t.offline.receiptNotPrinted, caught instanceof Error ? caught.message : undefined);
      } finally {
        onSettled();
      }
    };

    // One tick, so the sheet has laid out before it is photographed. A timer
    // rather than an animation frame: a background window never gets a frame
    // and the customer is standing at the counter.
    const timer = setTimeout(() => void run(), 30);
    return () => clearTimeout(timer);
  }, [config, offlineId, sale, onSettled, t]);

  if (!config) return null;

  return (
    <div aria-hidden="true" style={OFF_SCREEN} data-testid="offline-receipt-print">
      <div ref={wrapper}>
        <ReceiptSheet config={config} sale={sale} shop={shop} forPreview />
      </div>
    </div>
  );
}
